/* ============================================================
   Talention — SVPT embebido: reglas de importación desde Excel

   El backend convierte el fichero en filas (sin tocar el espacio). Aquí
   se cruzan esas filas con el espacio en memoria y se devuelve un PLAN:
   qué se crea, qué se actualiza y qué errores y avisos lleva cada fila.
   La pantalla enseña el plan y, al confirmar, un reducer lo aplica
   (spec docs/superpowers/specs/2026-09-29-svpt-importacion-excel-design.md).

   Funciones puras, sin React: se testean sin montar nada.
   ============================================================ */

import { romperCiclos } from "./hierarchy.js";
import { FACTOR_GROUPS, ITEM_TYPES, GENDERS, BINARY_LEVELS } from "./data.js";

const SIN_ASIGNAR = "Sin asignar";

/* Clave para casar nombres escritos a mano: sin acentos, en minúsculas y
   con los espacios colapsados. El texto original se conserva aparte. */
function normalizarNombre(texto) {
  return String(texto ?? "")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

const celda = (valor) => (valor == null ? "" : String(valor).trim());

const porFila = (a, b) => (a.fila ?? 0) - (b.fila ?? 0);

function agruparPorNombre(lista, nombreDe) {
  const grupos = new Map();
  for (const elemento of lista) {
    const clave = normalizarNombre(nombreDe(elemento));
    if (!grupos.has(clave)) grupos.set(clave, []);
    grupos.get(clave).push(elemento);
  }
  return grupos;
}

/* Id de un departamento o nivel existente, o la clave de uno que crea la
   importación. Cada nombre nuevo se apunta una sola vez. */
function resolverCatalogo(catalogo, nuevos, nombre) {
  const clave = normalizarNombre(nombre);
  const existente = catalogo.find(c => normalizarNombre(c.name) === clave);
  if (existente) return { id: existente.id };
  if (!nuevos.some(n => n.clave === clave)) nuevos.push({ clave, name: nombre });
  return { nuevo: clave };
}

/* ---- Puestos ---- */

function planificarImportacionPuestos(filas, espacio) {
  const errores = [];
  const avisos = [];
  const error = (fila, mensaje) => errores.push({ fila, mensaje });
  const aviso = (fila, mensaje) => avisos.push({ fila, mensaje });

  // Denominación: obligatoria y única dentro del Excel. Si se repite, no
  // se sabe qué fila manda, así que no entra ninguna.
  const conNombre = [];
  for (const f of filas) {
    if (!celda(f.denominacion)) error(f.fila, "Falta la denominación del puesto.");
    else conNombre.push(f);
  }
  const validas = [];
  for (const grupo of agruparPorNombre(conNombre, f => f.denominacion).values()) {
    if (grupo.length === 1) { validas.push(grupo[0]); continue; }
    const lista = grupo.map(f => f.fila).join(", ");
    for (const f of grupo) error(f.fila, `La denominación «${celda(f.denominacion)}» está repetida en las filas ${lista}.`);
  }
  validas.sort(porFila);

  const existentesPorNombre = agruparPorNombre(espacio.positions, p => p.name);
  const departamentosNuevos = [];
  const nivelesNuevos = [];
  const crear = [];
  const actualizar = [];

  for (const f of validas) {
    const name = celda(f.denominacion);
    const existentes = existentesPorNombre.get(normalizarNombre(name)) || [];
    if (existentes.length > 1) {
      error(f.fila, `Hay ${existentes.length} puestos llamados «${name}»: renombra uno antes de importar.`);
      continue;
    }
    const existente = existentes[0] || null;

    // Un puesto nuevo siempre tiene departamento y nivel: con la celda
    // vacía va a «Sin asignar». Uno existente con la celda vacía no cambia.
    const catalogo = (texto, lista, nuevos, etiqueta) => {
      if (texto) return resolverCatalogo(lista, nuevos, texto);
      if (existente) return null;
      aviso(f.fila, `Sin ${etiqueta}: se asigna a «${SIN_ASIGNAR}».`);
      return resolverCatalogo(lista, nuevos, SIN_ASIGNAR);
    };
    const departamento = catalogo(celda(f.departamento), espacio.departments, departamentosNuevos, "departamento");
    const nivel = catalogo(celda(f.nivel), espacio.levels, nivelesNuevos, "nivel jerárquico");

    let personas = null;
    const textoPersonas = celda(f.personas);
    if (/^\d+$/.test(textoPersonas)) personas = parseInt(textoPersonas, 10);
    else if (textoPersonas) {
      aviso(f.fila, `«${textoPersonas}» no es un número de personas válido: se deja ${existente ? "sin cambios" : "vacío"}.`);
    }

    const entrada = {
      fila: f.fila, name, departamento, nivel,
      descripcion: celda(f.descripcion) || null,
      personas,
      superior: null,
      dependeDe: celda(f.dependeDe),
    };
    if (existente) actualizar.push({ ...entrada, id: existente.id });
    else crear.push({ ...entrada, clave: normalizarNombre(name) });
  }

  resolverSuperiores(crear, actualizar, espacio, error, aviso);

  // `dependeDe` solo hacía falta para resolver: el plan lleva `superior`.
  const sinTexto = ({ dependeDe, ...resto }) => resto;
  return {
    crear: crear.map(sinTexto),
    actualizar: actualizar.map(sinTexto),
    departamentosNuevos,
    nivelesNuevos,
    errores: errores.sort(porFila),
    avisos: avisos.sort(porFila),
  };
}

/* «Depende de» se busca primero entre los puestos del Excel y después
   entre los existentes. Luego se asignan todos los superiores pedidos sobre
   una simulación del organigrama y el ciclo se juzga sobre ese estado final,
   no fila a fila (spec §4.1): en cada ciclo real se descarta el superior de
   la fila más tardía. El reducer aplica el `superior` que decide el plan. */
function resolverSuperiores(crear, actualizar, espacio, error, aviso) {
  const delExcel = new Map();
  for (const c of crear) delExcel.set(c.clave, { nuevo: c.clave });
  for (const a of actualizar) delExcel.set(normalizarNombre(a.name), { id: a.id });
  const existentesPorNombre = agruparPorNombre(espacio.positions, p => p.name);

  // Ids provisionales negativos para los puestos nuevos: no chocan con los reales.
  const idProvisional = new Map(crear.map((c, i) => [c.clave, -(i + 1)]));
  const idDe = (ref) => ("id" in ref ? ref.id : idProvisional.get(ref.nuevo));
  const simulado = [
    ...espacio.positions.map(p => ({ id: p.id, reportsToId: p.reportsToId ?? null })),
    ...crear.map(c => ({ id: idProvisional.get(c.clave), reportsToId: null })),
  ];

  const entradas = [
    ...crear.map(c => ({ entrada: c, id: idProvisional.get(c.clave), esNuevo: true })),
    ...actualizar.map(a => ({ entrada: a, id: a.id, esNuevo: false })),
  ].sort((x, y) => x.entrada.fila - y.entrada.fila);

  const pedidos = [];
  for (const { entrada, id, esNuevo } of entradas) {
    if (!entrada.dependeDe) continue;
    const clave = normalizarNombre(entrada.dependeDe);
    const existentes = existentesPorNombre.get(clave) || [];
    const ref = delExcel.get(clave) || (existentes.length === 1 ? { id: existentes[0].id } : null);
    if (!ref) {
      const consecuencia = esNuevo ? "queda sin superior" : "conserva su superior actual";
      const motivo = existentes.length > 1
        ? `Hay ${existentes.length} puestos llamados «${entrada.dependeDe}» en «Depende de»: renombra uno`
        : `No se encuentra el puesto «${entrada.dependeDe}» de «Depende de»`;
      aviso(entrada.fila, `${motivo}${existentes.length > 1 ? ";" : ":"} ${consecuencia}.`);
      continue;
    }
    const nodo = simulado.find(p => p.id === id);
    pedidos.push({ id, fila: entrada.fila, anterior: nodo.reportsToId, entrada });
    nodo.reportsToId = idDe(ref);
    entrada.superior = ref;
  }

  const porId = new Map(pedidos.map(p => [p.id, p]));
  for (const id of romperCiclos(simulado, pedidos)) {
    const { entrada } = porId.get(id);
    entrada.superior = null;
    error(entrada.fila, `«Depende de» «${entrada.dependeDe}» crearía un ciclo en el organigrama: el puesto se importa sin ese superior.`);
  }
}

/* ---- Factores ---- */

/* Un factor importado no toca el catálogo: se crea como factor
   personalizado. La hoja Niveles referencia los factores solo por nombre,
   así que un nombre repetido en la hoja Factores es ambiguo aunque esté
   en grupos distintos, y no entra ninguno. */
function planificarImportacionFactores(factores, niveles, espacio) {
  const errores = [];
  const avisos = [];
  const error = (fila, mensaje) => errores.push({ fila, mensaje });
  const aviso = (fila, mensaje) => avisos.push({ fila, mensaje });

  const grupoPorNombre = new Map();
  for (const g of FACTOR_GROUPS) {
    grupoPorNombre.set(normalizarNombre(g.name), g.id);
    grupoPorNombre.set(normalizarNombre(g.short), g.id);
  }
  const tipoPorNombre = new Map(ITEM_TYPES.map(t => [normalizarNombre(t.id), t.id]));
  const generoPorNombre = new Map(GENDERS.map(g => [normalizarNombre(g.id), g.id]));

  const conNombre = factores.filter(f => celda(f.factor));
  const repetidos = new Set();
  for (const [clave, grupo] of agruparPorNombre(conNombre, f => f.factor)) {
    if (grupo.length < 2) continue;
    repetidos.add(clave);
    const lista = grupo.map(f => f.fila).join(", ");
    for (const f of grupo) error(f.fila, `El factor «${celda(f.factor)}» está repetido en las filas ${lista}.`);
  }

  const candidatos = [];
  const omitidos = [];
  for (const f of factores) {
    const name = celda(f.factor);
    const textoGrupo = celda(f.grupo);
    const textoTipo = celda(f.tipo);
    const textoGenero = celda(f.genero);
    const groupId = grupoPorNombre.get(normalizarNombre(textoGrupo));
    const type = textoTipo ? tipoPorNombre.get(normalizarNombre(textoTipo)) : "escala";
    const gender = textoGenero ? generoPorNombre.get(normalizarNombre(textoGenero)) : "NEUTRO";

    let valido = true;
    const falla = (mensaje) => { error(f.fila, mensaje); valido = false; };
    if (!textoGrupo) falla("Falta el grupo del factor.");
    else if (!groupId) falla(`El grupo «${textoGrupo}» no existe. Usa uno de: ${FACTOR_GROUPS.map(g => g.name).join(", ")}.`);
    if (!name) falla("Falta el nombre del factor.");
    if (!type) falla(`El tipo «${textoTipo}» no es válido: usa escala o binario.`);
    if (!gender) falla(`El género «${textoGenero}» no es válido: usa NEUTRO, MASCULINO o FEMENINO.`);
    if (!valido || repetidos.has(normalizarNombre(name))) continue;

    const yaExiste = (espacio.factors.items[groupId] || []).some(it => normalizarNombre(it.name) === normalizarNombre(name));
    if (yaExiste) {
      const grupo = FACTOR_GROUPS.find(g => g.id === groupId);
      aviso(f.fila, `El factor «${name}» ya existe en el grupo ${grupo.name}: no se importa.`);
      omitidos.push({ fila: f.fila, groupId, name });
      continue;
    }
    candidatos.push({ fila: f.fila, groupId, name, desc: celda(f.descripcion), type, gender });
  }

  const nivelesPorFactor = agruparNiveles(niveles, conNombre, error);

  const crear = [];
  for (const c of candidatos) {
    const propios = nivelesPorFactor.get(normalizarNombre(c.name)) || { validos: [], conErrores: false };
    if (propios.conErrores) {
      error(c.fila, `El factor «${c.name}» no se importa porque tiene niveles con errores.`);
      continue;
    }
    let levels;
    if (c.type === "binario") {
      if (propios.validos.length > 0) aviso(c.fila, `El factor «${c.name}» es binario: se ignoran los niveles de la hoja Niveles y se usa No / Sí.`);
      levels = BINARY_LEVELS.map(l => ({ ...l }));
    } else if (propios.validos.length === 0) {
      aviso(c.fila, `El factor «${c.name}» no trae niveles: se crea «Sin niveles» y no se podrá valorar hasta definirlos.`);
      levels = [];
    } else if (propios.validos.length === 1) {
      error(c.fila, `El factor «${c.name}» de tipo escala necesita al menos 2 niveles.`);
      continue;
    } else {
      levels = [...propios.validos]
        .sort((a, b) => a.orden - b.orden)
        .map((l, i) => ({ id: `L${i}`, label: l.etiqueta, desc: l.descripcion }));
    }
    crear.push({ fila: c.fila, groupId: c.groupId, item: { name: c.name, desc: c.desc, type: c.type, gender: c.gender, levels } });
  }

  return { crear, omitidos, errores: errores.sort(porFila), avisos: avisos.sort(porFila) };
}

/* Niveles agrupados por factor. Un factor con algún nivel mal se marca
   `conErrores` y no se importa: crearlo con los niveles que sí están bien
   daría una escala distinta de la que se escribió. */
function agruparNiveles(niveles, factoresConNombre, error) {
  const factoresDeLaHoja = new Set(factoresConNombre.map(f => normalizarNombre(f.factor)));
  const porFactor = new Map();
  const de = (clave) => {
    if (!porFactor.has(clave)) porFactor.set(clave, { validos: [], conErrores: false, filasPorOrden: new Map() });
    return porFactor.get(clave);
  };

  for (const n of niveles) {
    const nombre = celda(n.factor);
    const clave = normalizarNombre(nombre);
    if (!nombre) { error(n.fila, "Niveles: falta el factor del nivel."); continue; }
    if (!factoresDeLaHoja.has(clave)) { error(n.fila, `Niveles: el factor «${nombre}» no está en la hoja Factores.`); continue; }
    const grupo = de(clave);
    const textoOrden = celda(n.orden);
    const etiqueta = celda(n.etiqueta);
    if (!/^-?\d+$/.test(textoOrden)) {
      error(n.fila, textoOrden ? `Niveles: el orden «${textoOrden}» no es un número entero.` : "Niveles: falta el orden del nivel.");
      grupo.conErrores = true;
      continue;
    }
    if (!etiqueta) {
      error(n.fila, "Niveles: falta la etiqueta del nivel.");
      grupo.conErrores = true;
      continue;
    }
    const orden = parseInt(textoOrden, 10);
    if (!grupo.filasPorOrden.has(orden)) grupo.filasPorOrden.set(orden, []);
    grupo.filasPorOrden.get(orden).push(n.fila);
    grupo.validos.push({ fila: n.fila, orden, etiqueta, descripcion: celda(n.descripcion) });
  }

  for (const [clave, grupo] of porFactor) {
    for (const [orden, filas] of grupo.filasPorOrden) {
      if (filas.length < 2) continue;
      const nombre = celda(factoresConNombre.find(f => normalizarNombre(f.factor) === clave).factor);
      for (const fila of filas) error(fila, `Niveles: el orden ${orden} está repetido en el factor «${nombre}».`);
      grupo.conErrores = true;
    }
  }
  return porFactor;
}

export { SIN_ASIGNAR, normalizarNombre, planificarImportacionPuestos, planificarImportacionFactores };
